import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'

type Config = {
  id?: number
  defaultCleaningTimeMinutes: number
  autoAllocationEnabled: number
  autoReleaseEnabled: number
  allowOverbooking: number
  bufferTimeMinutes: number
  maxTablesPerCombination: number
  maxWastePercentage: number
  preferFewerTables: number
}

type Props = {
  show: boolean
  onClose: () => void
  onSave: (data: Omit<Config, 'id'>) => void
  config?: Config
  environmentName: string
}

export const TableConfigModal: FC<Props> = ({show, onClose, onSave, config, environmentName}) => {
  const [formData, setFormData] = useState<Omit<Config, 'id'>>({
    defaultCleaningTimeMinutes: 15,
    autoAllocationEnabled: 1,
    autoReleaseEnabled: 0,
    allowOverbooking: 0,
    bufferTimeMinutes: 30,
    maxTablesPerCombination: 5,
    maxWastePercentage: 50,
    preferFewerTables: 1,
  })

  useEffect(() => {
    if (show && config) {
      setFormData({
        defaultCleaningTimeMinutes: config.defaultCleaningTimeMinutes,
        autoAllocationEnabled: config.autoAllocationEnabled,
        autoReleaseEnabled: config.autoReleaseEnabled,
        allowOverbooking: config.allowOverbooking,
        bufferTimeMinutes: config.bufferTimeMinutes,
        maxTablesPerCombination: config.maxTablesPerCombination ?? 5,
        maxWastePercentage: config.maxWastePercentage ?? 50,
        preferFewerTables: config.preferFewerTables ?? 1,
      })
    }
  }, [show, config])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            Configurações de Gestão de Mesas
            {environmentName && <small className='text-muted ms-2'>- {environmentName}</small>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-info d-flex align-items-center'>
            <i className='bi bi-info-circle fs-3 me-3'></i>
            <div>
              <strong>Configurações por Ambiente</strong>
              <div className='small'>
                Estas configurações são específicas para este ambiente e afetam o comportamento
                automático do sistema de gestão de mesas.
              </div>
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Tempo de Limpeza Padrão (minutos)</label>
            <input
              type='number'
              className='form-control'
              value={formData.defaultCleaningTimeMinutes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defaultCleaningTimeMinutes: Number(e.target.value),
                })
              }
              min='0'
              step='5'
            />
            <div className='form-text'>
              Tempo necessário para limpar e preparar a mesa após a liberação. Mesas em limpeza não
              são alocadas automaticamente.
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Tempo de Buffer (minutos)</label>
            <input
              type='number'
              className='form-control'
              value={formData.bufferTimeMinutes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bufferTimeMinutes: Number(e.target.value),
                })
              }
              min='0'
              step='5'
            />
            <div className='form-text'>
              Tempo de segurança entre reservas consecutivas. Garante que haja folga entre
              atendimentos.
            </div>
          </div>

          <div className='separator my-7'></div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='autoAllocationEnabled'
                checked={formData.autoAllocationEnabled === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    autoAllocationEnabled: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='autoAllocationEnabled'>
                <strong>Alocação Automática de Mesas</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              Quando ativado, o sistema automaticamente seleciona e aloca as melhores mesas para
              cada reserva com base na capacidade e disponibilidade. Você ainda pode fazer
              alocações manuais quando necessário.
            </div>
          </div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='autoReleaseEnabled'
                checked={formData.autoReleaseEnabled === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    autoReleaseEnabled: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='autoReleaseEnabled'>
                <strong>Liberação Automática de Mesas</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              Quando ativado, as mesas são automaticamente liberadas (mudadas para status de
              limpeza) após a confirmação de pagamento no WebApp. Desative se preferir liberar
              manualmente.
            </div>
          </div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='allowOverbooking'
                checked={formData.allowOverbooking === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    allowOverbooking: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='allowOverbooking'>
                <strong>Permitir Superlotação (Overbooking)</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              <span className='text-warning'>
                <i className='bi bi-exclamation-triangle me-1'></i>
                Use com cautela!
              </span>{' '}
              Quando ativado, permite aceitar reservas mesmo quando não há mesas disponíveis. Útil
              para estabelecimentos que praticam overbooking controlado.
            </div>
          </div>

          <div className='separator my-7'></div>

          <div className='mb-4'>
            <h5 className='mb-3'>
              <i className='bi bi-grid-3x3-gap me-2 text-primary'></i>
              Configurações de Unificação de Mesas
            </h5>
            <div className='alert alert-light-primary d-flex align-items-start'>
              <i className='bi bi-lightbulb fs-3 me-3 text-primary'></i>
              <div className='small'>
                <strong>Como funciona:</strong> Quando um grupo precisa de mais lugares do que uma mesa oferece, 
                o sistema pode unir várias mesas. Estas configurações controlam como essa unificação é feita.
              </div>
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Máximo de Mesas por Combinação</label>
            <input
              type='number'
              className='form-control'
              value={formData.maxTablesPerCombination}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxTablesPerCombination: Number(e.target.value),
                })
              }
              min='1'
              max='10'
              step='1'
            />
            <div className='form-text'>
              Número máximo de mesas que podem ser unidas para um único grupo (1-10).
              <div className='mt-1'>
                <strong>Exemplos:</strong>
                <ul className='mb-0 mt-1'>
                  <li><strong>3-4 mesas:</strong> Restaurante pequeno/médio (grupos até ~20 pessoas)</li>
                  <li><strong>5-7 mesas:</strong> Restaurante grande (grupos até ~35 pessoas)</li>
                  <li><strong>8-10 mesas:</strong> Eventos e banquetes (grupos muito grandes)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Desperdício Máximo de Capacidade (%)</label>
            <input
              type='number'
              className='form-control'
              value={formData.maxWastePercentage}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxWastePercentage: Number(e.target.value),
                })
              }
              min='0'
              max='100'
              step='5'
            />
            <div className='form-text'>
              Percentual máximo de lugares vazios aceitável em uma combinação (0-100%).
              <div className='mt-1'>
                <strong>Exemplo:</strong> Para um grupo de 10 pessoas com 50% de desperdício:
                <ul className='mb-0 mt-1'>
                  <li>✅ Aceita: 2 mesas de 8 lugares (16 total = 6 vazios = 37.5%)</li>
                  <li>❌ Rejeita: 3 mesas de 8 lugares (24 total = 14 vazios = 58%)</li>
                </ul>
              </div>
              <div className='mt-2'>
                <strong>Recomendações:</strong>
                <ul className='mb-0 mt-1'>
                  <li><strong>20-30%:</strong> Fine dining (otimizar espaço)</li>
                  <li><strong>40-50%:</strong> Restaurante padrão (balanceado)</li>
                  <li><strong>60-100%:</strong> Eventos (priorizar aceitar grupos)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='preferFewerTables'
                checked={formData.preferFewerTables === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    preferFewerTables: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='preferFewerTables'>
                <strong>Priorizar Usar Menos Mesas</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              <strong>Ativado (Recomendado):</strong> Sistema prefere usar menos mesas, mesmo que tenha um pouco mais de desperdício.
              Facilita operação e movimentação.
              <div className='mt-2'>
                <strong>Desativado:</strong> Sistema tenta todas as combinações para minimizar lugares vazios.
                Melhor aproveitamento do espaço, mas pode usar mais mesas que o necessário.
              </div>
              <div className='mt-2'>
                <strong>Exemplo para 12 pessoas:</strong>
                <ul className='mb-0 mt-1'>
                  <li><strong>Com prioridade:</strong> 2 mesas de 8 (16 lugares, 4 vazios) ✓</li>
                  <li><strong>Sem prioridade:</strong> 3 mesas de 4 (12 lugares, 0 vazios)</li>
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancelar
          </button>
          <button type='submit' className='btn btn-primary'>
            Salvar Configurações
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
